// Canonical taxonomy for KAfrequency daily activations.
// Series enum values match the Postgres `activation_series` type.
// Slot values match `activation_slot`.

export type ActivationSeries =
  | "resonance-calibration"
  | "attention-discipline"
  | "sacred-breath"
  | "mental-architecture"
  | "field-expansion"
  | "energetic-ownership"
  | "inner-government"
  | "right-action"
  | "sacred-momentum"
  | "embodied-mastery";

export type ActivationSlot =
  | "rising"
  | "midday"
  | "evening"
  | "night-seal"
  | "anytime";

export type ActivationStatus = "draft" | "scheduled" | "published";

export const ACTIVATION_SERIES: {
  slug: ActivationSeries;
  number: number;
  label: string;
  tagline: string;
}[] = [
  { slug: "resonance-calibration", number: 1, label: "Resonance Calibration", tagline: "Tune the instrument before the day plays it." },
  { slug: "attention-discipline",  number: 2, label: "Attention Discipline",  tagline: "Return authorship over what you attend to." },
  { slug: "sacred-breath",         number: 3, label: "Sacred Breath",         tagline: "The first law the body agrees to obey." },
  { slug: "mental-architecture",   number: 4, label: "Mental Architecture",   tagline: "Build the interior the mind can inhabit." },
  { slug: "field-expansion",       number: 5, label: "Field Expansion",       tagline: "Widen the room before you enter it." },
  { slug: "energetic-ownership",   number: 6, label: "Energetic Ownership",   tagline: "Nothing enters what you have not sealed." },
  { slug: "inner-government",      number: 7, label: "Inner Government",      tagline: "Rule the interior with mercy and law." },
  { slug: "right-action",          number: 8, label: "Right Action",          tagline: "The correct move made without noise." },
  { slug: "sacred-momentum",       number: 9, label: "Sacred Momentum",       tagline: "Discipline as devotion, day after day." },
  { slug: "embodied-mastery",     number: 10, label: "Embodied Mastery",      tagline: "The teaching walking in the world as you." },
];

export const SERIES_BY_SLUG: Record<ActivationSeries, typeof ACTIVATION_SERIES[number]> =
  Object.fromEntries(ACTIVATION_SERIES.map((s) => [s.slug, s])) as never;

export const ACTIVATION_SLOTS: { slug: ActivationSlot; label: string; hour: number; minute: number }[] = [
  { slug: "rising",     label: "Rising · 5:00 AM",   hour: 5,  minute: 0 },
  { slug: "midday",     label: "Midday · 11:00 AM",  hour: 11, minute: 0 },
  { slug: "evening",    label: "Evening · 5:00 PM",  hour: 17, minute: 0 },
  { slug: "night-seal", label: "Night Seal · 7:47 PM", hour: 19, minute: 47 },
  { slug: "anytime",    label: "Anytime",            hour: 0,  minute: 0 },
];

export const SLOT_BY_SLUG: Record<ActivationSlot, typeof ACTIVATION_SLOTS[number]> =
  Object.fromEntries(ACTIVATION_SLOTS.map((s) => [s.slug, s])) as never;

/** Build an ISO timestamp for a given date + slot in the browser's local timezone. */
export function publishAtForSlot(dateISO: string, slot: ActivationSlot): string {
  const [y, m, d] = dateISO.split("-").map(Number);
  const s = SLOT_BY_SLUG[slot];
  const dt = new Date(y, (m || 1) - 1, d || 1, s.hour, s.minute, 0);
  return dt.toISOString();
}

/** Format an ISO datetime for public display. */
export function formatPublishDate(iso: string | null): string {
  if (!iso) return "Unscheduled";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unscheduled";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export function formatPublishTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
